/**
 * Words for a raw API value, in the user's language: a status ("COMPLETED") or a role ("ADMIN").
 * A badge or a cell must never print the enum, so pages call these with the `t` of useI18n():
 *
 *   statusLabel(t, order.status)   // "Tugallangan" · "Завершён" · "Completed"
 *   roleLabel(t, user.role)        // "Admin" · "O'qituvchi"
 *
 * Lookup ignores case and separators ('IN_PROGRESS', 'in-progress' and 'inProgress' match the same
 * word). A value with no word yet is shown as plain sentence case ("Needs review"), never as the
 * upper-case enum and never as a raw i18n key.
 */
import statusNs from '../i18n/ns/status';
import rolesNs from '../i18n/ns/roles';

const compact = (value) => String(value ?? '').trim().toLowerCase().replace(/[\s_-]+/g, '');

const indexKeys = (dict) => Object.fromEntries(Object.keys(dict).map((key) => [compact(key), key]));

const STATUS_KEYS = indexKeys(statusNs.en);
const ROLE_KEYS = indexKeys(rolesNs.en);
// The CRM team roles live in teams.roleName.<ENUM>
const TEAM_ROLES = ['ADMIN', 'SALES', 'CALL_CENTER', 'CONTROL', 'ACCOUNT_MANAGER', 'MENTOR', 'STAFF'];
const TEAM_ROLE_KEYS = Object.fromEntries(TEAM_ROLES.map((role) => [compact(role), role]));

/** "in_progress" / "IN-PROGRESS" → "In progress": the fallback for a value with no word. */
export function humanize(value) {
  const spaced = String(value ?? '').replace(/[_-]+/g, ' ').trim().toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// t(key) hands the key back when there is no translation (or no provider): treat that as a miss.
function say(t, key) {
  const text = typeof t === 'function' ? t(key) : key;
  return typeof text === 'string' && text !== key ? text : null;
}

/** The status word for a raw value, in the user's language. Empty for a missing value. */
export function statusLabel(t, value) {
  if (value === null || value === undefined || value === '') return '';
  const key = STATUS_KEYS[compact(value)];
  return (key && say(t, `status.${key}`)) || humanize(value);
}

/** The role name for a raw value, in the user's language. Empty for a missing value. */
export function roleLabel(t, role) {
  if (role === null || role === undefined || role === '') return '';
  const account = ROLE_KEYS[compact(role)];
  if (account) {
    const text = say(t, `roles.${account}`);
    if (text) return text;
  }
  const team = TEAM_ROLE_KEYS[compact(role)];
  if (team) {
    const text = say(t, `teams.roleName.${team}`);
    if (text) return text;
  }
  return humanize(role);
}
